import React, { useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import Modal from "../../ui/UIElements/Modal";
import { toast } from "sonner";
import { useToggleUserStatus } from "../../../hooks/useToggleUserStatus";
import { useUserDetail } from "../../../hooks/useUserDetail";
import { useUsers } from "../../../hooks/useUsers";
import { useGlobalConfigs } from "@finitless/shared/hooks";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import WalletsTab from "../../ui/UserDetailsModal/WalletsTab";
import LoyaltyPointsTab from "../../ui/UserDetailsModal/LoyaltyPointsTab";
import OverviewTab from "../../ui/UserDetailsModal/OverviewTab";
import OrderHistoryTab from "../../ui/UserDetailsModal/OrderHistoryTab";
import AddressesTab from "../../ui/UserDetailsModal/AddressesTab";
import { Button } from "../../ui/UIElements/Button";
import {
  Tabs,
  TabsList,
  TabsPanel,
  TabsTrigger,
} from "../../ui/Navigation/Tabs";
import {
  getUserName,
  getUserInitials,
  getUserOrdersCount,
  getUserStatusBadge,
  getUserStatusBadgeColor,
  getVisibleTabsForUserLevel,
  isTabVisibleForUserLevel,
  USER_LEVELS,
} from "../../../constants/userConstants";
import "./customer-pages.css";

const CustomerDetailPage = ({
  initialUser = null,
  basePath = "/users",
  baseLabel = null,
  idLabelKey = "detail.labels.customerId",
}) => {
  const { t } = useTranslation(["customers", "common"]);
  const { id } = useParams();
  const userId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // Toggle user status hook
  const toggleUserStatusMutation = useToggleUserStatus();

  // Get global configs for wallet feature check
  const { data: configs } = useGlobalConfigs();

  // Get user data from navigation state or props
  const userFromState = location.state?.user;
  const userData = initialUser || userFromState;

  // Fetch user data using custom hook
  const {
    user: currentUser,
    isLoading,
    error,
    isError,
    updateUser,
    refetch: refetchUser,
    isFetching,
  } = useUserDetail(userId, userData);

  // Get visible tabs based on user level and wallet config
  const visibleTabs = useMemo(() => {
    return getVisibleTabsForUserLevel(currentUser?.level, configs);
  }, [currentUser?.level, configs]);

  // Fetch users for transaction history author lookup - only if wallets tab is visible
  const shouldFetchUsers = currentUser
    ? isTabVisibleForUserLevel("wallets", currentUser.level, configs)
    : false;
  const { data: usersData } = useUsers({
    page_size: 1000,
    enabled: shouldFetchUsers,
  }); // Get all users for author lookup only if needed

  const handleToggleUserStatus = () => {
    if (currentUser.enabled) {
      setShowDeactivateModal(true);
    } else {
      handleConfirmToggle();
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !currentUser.enabled;

      await toggleUserStatusMutation.mutateAsync({
        userId: currentUser.id,
        enabled: newEnabledStatus,
      });

      updateUser((prev) => ({
        ...prev,
        enabled: newEnabledStatus,
      }));

      toast.success("Status Updated", {
        description: t("detail.toasts.statusUpdated", {
          status: newEnabledStatus
            ? t("detail.states.activated")
            : t("detail.states.deactivated"),
        }),
        duration: 3000,
      });

      setShowDeactivateModal(false);
    } catch {
      toast.error(t("toasts.updateFailedTitle"), {
        description: t("detail.toasts.statusUpdateFailed"),
        duration: 3000,
      });
    }
  };

  const handleBackToCustomers = () => {
    navigate(basePath);
  };

  const handleRefreshUser = async () => {
    try {
      await refetchUser();
      toast.success(t("detail.toasts.userRefreshedTitle"), {
        description: t("detail.toasts.userRefreshedDescription"),
        duration: 3000,
      });
    } catch {
      toast.error(t("detail.toasts.refreshFailedTitle"), {
        description: t("detail.toasts.refreshFailedDescription"),
        duration: 3000,
      });
    }
  };

  if (isLoading) {
    return (
      <div
        className="ordering-customer-detail flex min-h-96 items-center justify-center p-4"
        data-ordering-surface="customer-detail-loading"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="border-primary mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 motion-reduce:animate-none sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("detail.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !currentUser)) {
    return (
      <div
        className="ordering-customer-detail border-border bg-card rounded-lg border px-4 py-8 text-center sm:py-12"
        data-ordering-surface="customer-detail-error"
        role="alert"
      >
        <Icon
          name="UserX"
          className="text-muted-foreground mx-auto mb-3 h-8 w-8 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="text-foreground mt-2 text-sm font-medium sm:text-base">
          {t("detail.states.notFoundTitle")}
        </h3>
        <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
          {error?.message || t("detail.states.notFoundDescription")}
        </p>
        <Button
          type="button"
          onClick={handleBackToCustomers}
          emphasis="solid"
          intent="brand"
          size="touch"
          className="mt-3 sm:mt-4"
        >
          {basePath === "/users"
            ? t("detail.actions.backToCustomers")
            : t("adminsPage.detail.actions.backToAdmins")}
        </Button>
      </div>
    );
  }

  const name = getUserName(currentUser);
  const initials = getUserInitials(currentUser);
  const ordersCount = getUserOrdersCount(currentUser);
  const statusBadge = getUserStatusBadge(currentUser.enabled);
  const statusBadgeColor = getUserStatusBadgeColor(currentUser.enabled);

  // Only show tabs if there's more than one (overview + others)
  const shouldShowTabs = visibleTabs.length > 1;

  const breadcrumbItems = [
    {
      label: baseLabel || t("header.title"),
      path: basePath,
      icon: basePath === "/users" ? "Users" : "Shield",
    },
    {
      label: currentUser
        ? getUserName(currentUser)
        : t("states.loading", { ns: "common" }),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div
      className="ordering-customer-detail space-y-4 sm:space-y-6"
      data-ordering-surface="customer-detail"
      data-customer-mode={basePath === "/users" ? "customer" : "admin"}
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* User Profile Header */}
      <div className="border-border bg-card rounded-lg border shadow-[var(--od-shadow-1)]">
        <div className="border-border border-b p-3 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="bg-primary flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-16 sm:w-16">
                {currentUser.photo ? (
                  <img
                    src={currentUser.photo}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`bg-primary text-primary-foreground flex h-full w-full items-center justify-center text-lg font-semibold sm:text-2xl ${
                    currentUser.photo ? "hidden" : "flex"
                  }`}
                >
                  {initials}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-foreground truncate text-lg font-bold sm:text-2xl">
                  {name}
                </h1>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {t(idLabelKey, { id: currentUser.id })}
                </p>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
              <div className="flex items-center space-x-2">
                <span className="text-muted-foreground text-xs sm:text-sm">
                  {t("detail.labels.status")}:
                </span>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium sm:px-3 sm:text-sm ${statusBadgeColor}`}
                >
                  {statusBadge}
                </span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button
                  type="button"
                  onClick={handleRefreshUser}
                  disabled={isFetching}
                  aria-busy={isFetching || undefined}
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                >
                  {isFetching ? (
                    <div className="border-foreground h-3 w-3 animate-spin rounded-full border-b-2 motion-reduce:animate-none sm:h-4 sm:w-4"></div>
                  ) : (
                    <Icon name="RefreshCw" className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                  <span className="sm:inline">
                    {isFetching
                      ? t("detail.actions.refreshing")
                      : t("detail.actions.refresh")}
                  </span>
                </Button>
                <Button
                  type="button"
                  onClick={handleToggleUserStatus}
                  disabled={toggleUserStatusMutation.isPending}
                  aria-busy={toggleUserStatusMutation.isPending || undefined}
                  emphasis="solid"
                  intent={currentUser.enabled ? "warning" : "success"}
                  size="touch"
                >
                  <Icon
                    name={currentUser.enabled ? "UserX" : "UserCheck"}
                    className="h-3 w-3 sm:h-4 sm:w-4"
                  />
                  <span className="hidden sm:inline">
                    {toggleUserStatusMutation.isPending
                      ? t("detail.actions.updating")
                      : currentUser.enabled
                        ? t("detail.actions.deactivateUser")
                        : t("detail.actions.activateUser")}
                  </span>
                  <span className="sm:hidden">
                    {toggleUserStatusMutation.isPending
                      ? t("detail.actions.updating")
                      : currentUser.enabled
                        ? t("detail.actions.deactivate")
                        : t("detail.actions.activate")}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Only show if there are multiple tabs */}
        {shouldShowTabs ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList
              aria-label={baseLabel || t("header.title")}
              className="px-3 sm:px-6"
            >
              {visibleTabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.labelKey ? t(tab.labelKey) : tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="ordering-customer-detail__content p-3 sm:p-6">
              <TabsPanel value="overview">
                <OverviewTab
                  user={currentUser}
                  ordersCount={ordersCount}
                  showStats={currentUser.level !== USER_LEVELS.ADMIN}
                />
              </TabsPanel>

              {isTabVisibleForUserLevel(
                "order-history",
                currentUser.level,
                configs,
              ) && (
                <TabsPanel value="order-history">
                  <OrderHistoryTab userId={currentUser?.id} />
                </TabsPanel>
              )}

              {isTabVisibleForUserLevel(
                "wallets",
                currentUser.level,
                configs,
              ) && (
                <TabsPanel value="wallets">
                  <WalletsTab
                    wallets={currentUser?.wallets}
                    userId={currentUser?.id}
                    users={usersData?.users || []}
                  />
                </TabsPanel>
              )}

              {isTabVisibleForUserLevel(
                "loyalty-points",
                currentUser.level,
                configs,
              ) && (
                <TabsPanel value="loyalty-points">
                  <LoyaltyPointsTab loyaltyLevel={currentUser?.loyalty_level} />
                </TabsPanel>
              )}

              {isTabVisibleForUserLevel(
                "addresses",
                currentUser.level,
                configs,
              ) && (
                <TabsPanel value="addresses">
                  <AddressesTab
                    addresses={currentUser?.addresses}
                    userId={currentUser?.id}
                    onUpdate={() => refetchUser()}
                  />
                </TabsPanel>
              )}
            </div>
          </Tabs>
        ) : (
          <div className="ordering-customer-detail__content p-3 sm:p-6">
            <OverviewTab
              user={currentUser}
              ordersCount={ordersCount}
              showStats={currentUser.level !== USER_LEVELS.ADMIN}
            />
          </div>
        )}
      </div>

      {/* Deactivate User Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title={t("detail.deactivateModal.title")}
        subtitle={t("detail.deactivateModal.subtitle")}
        maxWidth="max-w-md"
      >
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--warning-bg)] sm:h-10 sm:w-10">
                <Icon
                  name="AlertTriangle"
                  className="h-4 w-4 text-[var(--warning)] sm:h-5 sm:w-5"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-foreground text-sm font-medium">
                {t("detail.deactivateModal.confirmName", { name })}
              </h3>
              <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                {t("detail.deactivateModal.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <Button
              type="button"
              onClick={handleConfirmToggle}
              disabled={toggleUserStatusMutation.isPending}
              aria-busy={toggleUserStatusMutation.isPending || undefined}
              emphasis="solid"
              intent="warning"
              size="touch"
              className="flex-1"
            >
              {toggleUserStatusMutation.isPending ? (
                <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-[var(--warning-foreground)] motion-reduce:animate-none sm:h-4 sm:w-4"></div>
              ) : null}
              <span>
                {toggleUserStatusMutation.isPending
                  ? t("detail.actions.deactivating")
                  : t("detail.actions.deactivateUser")}
              </span>
            </Button>
            <Button
              type="button"
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleUserStatusMutation.isPending}
              emphasis="outline"
              intent="neutral"
              size="touch"
              className="flex-1"
            >
              {t("actions.cancel", { ns: "common" })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerDetailPage;
